import { useEffect, useState } from "react";

import { Note } from "../components/Note";
import { PaymentCard } from "../components/PaymentCard";
import { RefundConfirm } from "../components/RefundConfirm";
import { useRefunds } from "../hooks/useRefunds";
import { t } from "../i18n/strings";

interface RefundsScreenProps {
  /** Set when an admin came here from a ticket, to look at that client's payments. */
  userId?: number;
}

const OUTCOME_MESSAGES = {
  done: "refunds.done",
  stars_failed: "refunds.starsFailed",
  clawback_failed: "refunds.clawbackFailed",
  rejected: "refunds.rejected",
  rejected_unnotified: "refunds.rejectedUnnotified",
} as const;

/** The outcomes which went as asked; the rest need the admin's attention. */
const SUCCESSFUL_OUTCOMES = new Set(["done", "rejected"]);

export function RefundsScreen({ userId }: RefundsScreenProps) {
  const refunds = useRefunds();
  const [query, setQuery] = useState(userId ? String(userId) : "");
  const [badUser, setBadUser] = useState(false);

  useEffect(() => {
    if (userId !== undefined) {
      setQuery(String(userId));
      refunds.loadClient(userId);
    }
    // deliberately only on a change of the client this screen was opened for
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  function find() {
    const parsed = Number(query.trim());
    if (!query.trim() || !Number.isInteger(parsed)) {
      setBadUser(true);
      return;
    }
    setBadUser(false);
    refunds.loadClient(parsed);
  }

  return (
    <section className="screen">
      <div className="lookup">
        <input
          className="lookup__input"
          value={query}
          inputMode="numeric"
          placeholder={t("refunds.userPlaceholder")}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              find();
            }
          }}
        />
        <button type="button" className="button" onClick={find}>
          {t("refunds.find")}
        </button>
        <button type="button" className="button button--quiet" onClick={refunds.loadQueue}>
          {t("refunds.queue")}
        </button>
      </div>

      {badUser ? <Note kind="error">{t("refunds.badUser")}</Note> : null}
      {refunds.error ? <Note kind="error">{refunds.error}</Note> : null}
      {refunds.outcome ? (
        <Note kind={SUCCESSFUL_OUTCOMES.has(refunds.outcome) ? "success" : "warning"}>
          {t(OUTCOME_MESSAGES[refunds.outcome])}
        </Note>
      ) : null}

      {refunds.preparation ? (
        <RefundConfirm
          preparation={refunds.preparation}
          confirming={refunds.confirming}
          rejecting={refunds.rejecting}
          error={refunds.confirmError}
          onConfirm={refunds.confirm}
          onReject={refunds.reject}
          onCancel={refunds.dismiss}
        />
      ) : null}

      {refunds.loading ? (
        <p className="screen__loading">{t("app.loading")}</p>
      ) : refunds.payments.length === 0 ? (
        <p className="screen__empty">
          {refunds.userId === null ? t("refunds.empty") : t("refunds.noPayments", { user: refunds.userId })}
        </p>
      ) : (
        <div className="payment-list">
          {refunds.payments.map((payment) => (
            <PaymentCard
              key={payment.id}
              payment={payment}
              disabled={refunds.preparing || refunds.confirming || refunds.rejecting || refunds.preparation !== null}
              onSelect={refunds.select}
            />
          ))}
        </div>
      )}
    </section>
  );
}
